import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveMembers, ActiveMember } from '@/hooks/useActiveMembers';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Building2, ChevronLeft, ChevronRight, Linkedin } from 'lucide-react';

function MemberPopoverCard({ member }: { member: ActiveMember }) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center gap-3 p-1">
      <Avatar className="h-16 w-16">
        <SignedAvatarImage src={member.avatar_url || undefined} />
        <AvatarFallback className="bg-gradient-primary text-white text-xl font-display">
          {member.full_name?.charAt(0) || 'U'}
        </AvatarFallback>
      </Avatar>

      <div className="text-center space-y-1">
        <p className="font-semibold text-sm">{member.full_name}</p>
        {member.headline && (
          <p className="text-xs text-muted-foreground">{member.headline}</p>
        )}
        {member.company && (
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
            <Building2 className="h-3 w-3" />
            {member.company}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 w-full">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 text-xs"
          onClick={() => navigate(`/members/${member.user_id}`)}
        >
          View Profile
        </Button>
        <Button
          size="sm"
          className="flex-1 text-xs"
          onClick={() => navigate(`/messages?to=${member.user_id}`)}
        >
          Message
        </Button>
        {member.linkedin_url && (
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 shrink-0"
            asChild
          >
            <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer">
              <Linkedin className="h-4 w-4" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

export function MemberAvatarBar({ cityCohort }: { cityCohort?: string } = {}) {
  const { members, isLoading } = useActiveMembers(cityCohort);
  const isMobile = useIsMobile();
  const scrollRef = useRef<HTMLDivElement>(null);

  if (isLoading || members.length === 0) return null;

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = 200;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  return (
    <div className="relative group">
      {/* Scroll arrows — desktop only */}
      {!isMobile && (
        <>
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/80 border shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/80 border shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide py-1 px-1"
      >
        {members.map((member) => (
          <Popover key={member.user_id}>
            <PopoverTrigger asChild>
              <button className="flex flex-col items-center gap-1 shrink-0 w-16 group/avatar">
                <div
                  className={`rounded-full p-[2px] ${
                    member.isRecentlyActive
                      ? 'bg-gradient-to-tr from-pink-500 via-purple-500 to-indigo-500'
                      : 'bg-border'
                  }`}
                >
                  <Avatar className="h-12 w-12 border-2 border-background">
                    <SignedAvatarImage src={member.avatar_url || undefined} />
                    <AvatarFallback className="bg-gradient-primary text-white text-sm font-display">
                      {member.full_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <span className="text-[11px] text-muted-foreground truncate w-full text-center group-hover/avatar:text-foreground transition-colors">
                  {member.full_name?.split(' ')[0]?.slice(0, 8) || 'Member'}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64" side="bottom" align="center">
              <MemberPopoverCard member={member} />
            </PopoverContent>
          </Popover>
        ))}
      </div>
    </div>
  );
}
